import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createLabExternalStorageProvider,
  disableLabExternalStorageProvider,
  getLabExternalStorageProviders,
  labExternalStorageProvidersQueryKey,
  renameLabExternalStorageProvider,
  updateLabExternalStorageProvider,
  validateLabExternalStorageProvider,
} from 'api/externalStorageProviders';
import { updateLab } from 'api/labs';
import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { LabExternalStorageProvider, LabExternalStorageProviderCreate } from 'interfaces/externalStorage';
import type { Study } from 'interfaces/study';
import { map } from 'lodash';
import React, { useState } from 'react';
import { formatDateTimeForUser } from 'utils/dateTime';
import { getQueryErrorMessage } from 'utils/httpError';
import { LabStudyDefaultsSettings } from './LabStudyDefaultsSettings';
import { ProviderCredentialsForm } from './ProviderCredentialsForm';

interface LabExternalStorageSettingsDialogProps {
  labId: string;
  labName?: string;
  currentLabStudyDefaultSettings?: Study['settings'];
  canManageExternalStorage: boolean;
  canEditStudyDefaults: boolean;
  open: boolean;
  onClose: () => void;
}

type EditorState = { mode: 'create' } | { mode: 'update'; providerName: string } | null;

export const LabExternalStorageSettingsDialog: React.FC<LabExternalStorageSettingsDialogProps> = ({
  labId,
  labName,
  currentLabStudyDefaultSettings,
  canManageExternalStorage,
  canEditStudyDefaults,
  open,
  onClose,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<EditorState>(null);
  const [selectedProviderName, setSelectedProviderName] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [validationBucket, setValidationBucket] = useState('');
  const [validationPrefix, setValidationPrefix] = useState('');
  const [actionMessage, setActionMessage] = useState<{ severity: 'error' | 'success'; text: string } | null>(null);
  const [labNameValue, setLabNameValue] = useState(labName || '');
  const [activeTab, setActiveTab] = useState<'study-defaults' | 'external-storage'>(
    canEditStudyDefaults ? 'study-defaults' : 'external-storage'
  );

  const providersQuery = useQuery({
    queryKey: labExternalStorageProvidersQueryKey(labId),
    queryFn: ({ signal }) => getLabExternalStorageProviders(labId, signal),
    enabled: open && canManageExternalStorage && activeTab === 'external-storage' && Boolean(labId),
  });

  const saveStudyDefaults = async (settings: Study['settings']) => {
    await updateLab({ labId, data: { defaultStudySettings: settings } });
    await queryClient.invalidateQueries({ queryKey: ['labs'] });
  };

  const labNameMutation = useMutation((nextLabName: string) => updateLab({ labId, data: { labName: nextLabName } }), {
    onSuccess: async (updatedLab) => {
      setLabNameValue(updatedLab.name);
      await queryClient.invalidateQueries({ queryKey: ['labs'] });
    },
  });

  const refreshProviders = async () => {
    await queryClient.invalidateQueries({ queryKey: labExternalStorageProvidersQueryKey(labId) });
  };

  const credentialsMutation = useMutation(
    (value: LabExternalStorageProviderCreate) =>
      editor?.mode === 'update'
        ? updateLabExternalStorageProvider(labId, editor.providerName, { credentials: value.credentials })
        : createLabExternalStorageProvider(labId, value),
    {
      onSuccess: async (provider) => {
        setEditor(null);
        setSelectedProviderName(provider.name);
        setRenameValue(provider.name);
        setValidationBucket('');
        setValidationPrefix('');
        setActionMessage({ severity: 'success', text: `Provider ${provider.name} saved.` });
        await refreshProviders();
      },
    }
  );

  const availabilityMutation = useMutation(
    (provider: LabExternalStorageProvider) =>
      provider.enabled
        ? disableLabExternalStorageProvider(labId, provider.name)
        : updateLabExternalStorageProvider(labId, provider.name, { enabled: true }),
    {
      onSuccess: async (provider) => {
        setActionMessage({
          severity: 'success',
          text: `Provider ${provider.name} ${provider.enabled ? 'enabled' : 'disabled'}.`,
        });
        await refreshProviders();
      },
      onError: (error) =>
        setActionMessage({
          severity: 'error',
          text: getQueryErrorMessage(error, 'Provider status could not be changed.'),
        }),
    }
  );

  const renameMutation = useMutation(
    ({ providerName, newName }: { providerName: string; newName: string }) =>
      renameLabExternalStorageProvider(labId, providerName, newName),
    {
      onSuccess: async (provider) => {
        setSelectedProviderName(provider.name);
        setRenameValue(provider.name);
        setActionMessage({ severity: 'success', text: `Provider renamed to ${provider.name}.` });
        await refreshProviders();
      },
      onError: (error) =>
        setActionMessage({ severity: 'error', text: getQueryErrorMessage(error, 'Provider could not be renamed.') }),
    }
  );

  const validationMutation = useMutation(
    ({ providerName, bucket, prefix }: { providerName: string; bucket: string; prefix?: string }) =>
      validateLabExternalStorageProvider(labId, providerName, { bucket, prefix }),
    {
      onSuccess: async (result) => {
        setActionMessage({
          severity: result.validationStatus === 'validated' ? 'success' : 'error',
          text:
            result.message ||
            `Provider access to ${result.bucket}${result.prefix ? `/${result.prefix}` : ''} validated.${
              result.acceleratedEndpointEnabled === false
                ? ' S3 Transfer Acceleration is not enabled for this bucket.'
                : ''
            }`,
        });
        await refreshProviders();
      },
      onError: (error) =>
        setActionMessage({ severity: 'error', text: getQueryErrorMessage(error, 'Provider validation failed.') }),
    }
  );

  const providers: LabExternalStorageProvider[] = providersQuery.data || [];
  const selectedProvider = providers.find((provider) => provider.name === selectedProviderName);
  const credentialsError = credentialsMutation.isError
    ? getQueryErrorMessage(credentialsMutation.error, 'Provider could not be saved.')
    : undefined;
  const subduedFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.45),
      transition: theme.transitions.create(['background-color', 'border-color'], {
        duration: theme.transitions.duration.shorter,
      }),
      '& fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.14),
      },
      '&:hover fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.24),
      },
      '&.Mui-focused fieldset': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
  };

  const selectProvider = (provider: LabExternalStorageProvider) => {
    setSelectedProviderName(provider.name);
    setRenameValue(provider.name);
    setValidationBucket('');
    setValidationPrefix('');
    setActionMessage(null);
    setEditor(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="lab-storage-settings-title">
      <DialogChromeHeader
        title="Lab settings"
        titleId="lab-storage-settings-title"
        closeButtonAriaLabel="Close lab settings"
        onClose={onClose}
      />
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ letterSpacing: 1.2, textTransform: 'uppercase' }}
            >
              Lab
            </Typography>
            {canEditStudyDefaults ? (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 0.5 }}>
                <TextField
                  label="Lab name"
                  value={labNameValue}
                  onChange={(event) => {
                    labNameMutation.reset();
                    setLabNameValue(event.target.value);
                  }}
                  error={labNameMutation.isError}
                  helperText={
                    labNameMutation.isError
                      ? getQueryErrorMessage(labNameMutation.error, 'Lab name could not be updated.')
                      : undefined
                  }
                  disabled={labNameMutation.isLoading}
                  size="small"
                  sx={{ ...subduedFieldStyles, minWidth: 280 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => labNameMutation.mutate(labNameValue.trim())}
                  disabled={
                    labNameMutation.isLoading || !labNameValue.trim() || labNameValue.trim() === (labName || '').trim()
                  }
                >
                  Save name
                </Button>
              </Stack>
            ) : (
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {labName || labId}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              ID: {labId}
            </Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(_, nextTab) => setActiveTab(nextTab)}
            variant="scrollable"
            allowScrollButtonsMobile
            aria-label="lab settings tabs"
          >
            {canEditStudyDefaults ? <Tab value="study-defaults" label="Study defaults" /> : null}
            {canManageExternalStorage ? <Tab value="external-storage" label="External storage" /> : null}
          </Tabs>

          {activeTab === 'study-defaults' && canEditStudyDefaults ? (
            <LabStudyDefaultsSettings
              key={labId}
              currentLabStudyDefaultSettings={currentLabStudyDefaultSettings}
              onSave={saveStudyDefaults}
            />
          ) : null}

          {activeTab === 'external-storage' && canManageExternalStorage ? (
            <Stack spacing={3}>
              <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
                <Grid item xs>
                  <Stack spacing={0.75}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      External storage providers
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure credential sets used to connect this lab to customer-managed storage.
                    </Typography>
                  </Stack>
                </Grid>
                {!editor ? (
                  <Grid item>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        credentialsMutation.reset();
                        setEditor({ mode: 'create' });
                        setSelectedProviderName(null);
                        setRenameValue('');
                        setActionMessage(null);
                      }}
                    >
                      Add provider
                    </Button>
                  </Grid>
                ) : null}
              </Grid>

              {providersQuery.isLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography color="text.secondary">Loading providers…</Typography>
                </Stack>
              ) : providersQuery.isError ? (
                <Alert severity="error">
                  {getQueryErrorMessage(providersQuery.error, 'Providers could not be loaded.')}
                </Alert>
              ) : providers.length === 0 && !editor ? (
                <Alert severity="info">No external storage providers are configured for this lab.</Alert>
              ) : null}

              {providers.length > 0 ? (
                <List disablePadding aria-label="external storage providers">
                  {map(providers, (provider, index) => (
                    <React.Fragment key={provider.name}>
                      {index > 0 ? <Divider /> : null}
                      <ListItem
                        disableGutters
                        secondaryAction={
                          <Button
                            size="small"
                            aria-label={`Manage ${provider.name}`}
                            onClick={() => selectProvider(provider)}
                          >
                            Manage
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography>{provider.name}</Typography>
                              <Chip
                                size="small"
                                color={provider.enabled ? 'success' : 'default'}
                                label={provider.enabled ? 'Enabled' : 'Disabled'}
                              />
                              {provider.validationStatus ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`Validation: ${provider.validationStatus}`}
                                />
                              ) : null}
                            </Stack>
                          }
                          secondary={
                            provider.updatedAt
                              ? `Updated ${formatDateTimeForUser(provider.updatedAt)}`
                              : 'No updates yet'
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              ) : null}

              {editor ? (
                <>
                  <Divider />
                  <ProviderCredentialsForm
                    key={`${editor.mode}-${editor.mode === 'update' ? editor.providerName : 'new'}`}
                    mode={editor.mode}
                    providerName={editor.mode === 'update' ? editor.providerName : undefined}
                    isSaving={credentialsMutation.isLoading}
                    submitError={credentialsError}
                    onCancel={() => {
                      setEditor(null);
                      credentialsMutation.reset();
                    }}
                    onSubmit={(value) => credentialsMutation.mutate(value)}
                  />
                </>
              ) : null}

              {selectedProvider && !editor ? (
                <>
                  <Divider />
                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ letterSpacing: 1.2, textTransform: 'uppercase' }}
                      >
                        Manage provider
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {selectedProvider.name}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          label="Provider name"
                          value={renameValue}
                          onChange={(event) => setRenameValue(event.target.value)}
                          fullWidth
                          sx={subduedFieldStyles}
                          helperText="Renaming migrates active study bucket-setting references. Historical jobs keep the old name."
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled={
                            !renameValue.trim() ||
                            renameValue.trim() === selectedProvider.name ||
                            renameMutation.isLoading
                          }
                          onClick={() =>
                            renameMutation.mutate({ providerName: selectedProvider.name, newName: renameValue.trim() })
                          }
                        >
                          Rename
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Test credentials
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {
                              'Enter a bucket and, if needed, a path within it. We use these values to verify this provider can access that location.'
                            }
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={5}>
                            <TextField
                              label="Bucket"
                              placeholder="customer-slide-bucket"
                              value={validationBucket}
                              onChange={(event) => setValidationBucket(event.target.value)}
                              fullWidth
                              required
                              size="small"
                              sx={subduedFieldStyles}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Path"
                              placeholder="Optional path"
                              value={validationPrefix}
                              onChange={(event) => setValidationPrefix(event.target.value)}
                              fullWidth
                              size="small"
                              sx={subduedFieldStyles}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Button
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{ minHeight: 40 }}
                              disabled={
                                !selectedProvider.enabled || !validationBucket.trim() || validationMutation.isLoading
                              }
                              onClick={() =>
                                validationMutation.mutate({
                                  providerName: selectedProvider.name,
                                  bucket: validationBucket.trim(),
                                  ...(validationPrefix.trim() ? { prefix: validationPrefix.trim() } : {}),
                                })
                              }
                            >
                              Test access
                            </Button>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {actionMessage ? <Alert severity={actionMessage.severity}>{actionMessage.text}</Alert> : null}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          credentialsMutation.reset();
                          setEditor({ mode: 'update', providerName: selectedProvider.name });
                          setActionMessage(null);
                        }}
                      >
                        Update credentials
                      </Button>
                      <Button
                        color={selectedProvider.enabled ? 'warning' : 'primary'}
                        variant="outlined"
                        disabled={availabilityMutation.isLoading}
                        onClick={() => availabilityMutation.mutate(selectedProvider)}
                      >
                        {selectedProvider.enabled ? 'Disable provider' : 'Enable provider'}
                      </Button>
                    </Stack>
                  </Stack>
                </>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogChromeFooter>
        <Button onClick={onClose}>Close</Button>
      </DialogChromeFooter>
    </Dialog>
  );
};
